import React from 'react';
import PropTypes from 'prop-types';
import { Popup, Icon, Label, Header, List, Segment } from 'semantic-ui-react';
import { VARIATION_STATUSES } from './variationStatusConfig';

/**
 * VariationStatusLegend
 *
 * Explains the colored/iconed variation status badges used on the
 * Manage Opportunities grid and related views. Serves as the non-color
 * affordance required for color-blind/color-deficient users.
 *
 * Two render modes:
 *
 *   <VariationStatusLegend compact />
 *       → small "info circle" button that opens a click-Popup
 *         containing the legend rows. Intended for placement next to
 *         column headers.
 *
 *   <VariationStatusLegend />
 *       → inline Segment rendering the legend directly, suitable
 *         for help pages or settings areas.
 */

const LegendRows = () => (
  <List relaxed="very" style={{ margin: 0 }}>
    {VARIATION_STATUSES.map((s) => (
      <List.Item
        key={s.key}
        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
      >
        {/* Mirror the layout used inside VariationReviewRow's status Label:
            inline-flex + flex-start + vertical center, with each child
            given matching `height` + `lineHeight` so the icon glyph and
            the text sit on a shared centerline.

            NOTE: we intentionally do NOT use the `top: -1px` optical
            nudge here — the legend Label sits on a larger line-box than
            the in-table badge, so the nudge would visibly top-justify
            the text. Matching `height` on both children is a more
            robust fix that works in both contexts. */}
        <Label
          size="tiny"
          color={s.color}
          style={{
            minWidth: '92px',
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '4px',
            lineHeight: 1,
            textAlign: 'left'
          }}
        >
          <Icon
            name={s.icon}
            style={{
              margin: 0,
              // Force the icon into the same 1em box as the text so
              // `alignItems: center` has a symmetrical line-box to
              // center against (Semantic's Icon ships with its own
              // line-height which otherwise skews the vertical middle
              // of this flex container upward).
              height: '1em',
              lineHeight: '1em',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <span
            style={{
              height: '1em',
              lineHeight: '1em',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            {s.label}
          </span>
        </Label>

        <div style={{ fontSize: '0.9em', lineHeight: 1.35 }}>
          <strong>{s.label}</strong> — {s.description}
        </div>
      </List.Item>
    ))}
  </List>
);


const VariationStatusLegend = ({
  compact = false,
  iconName = 'info circle',
  title = 'Variation Status Legend',
  style
}) => {
  if (!compact) {
    return (
      <Segment basic style={style}>
        <Header as="h5" style={{ marginTop: 0 }}>
          <Icon name={iconName} />
          <Header.Content>{title}</Header.Content>
        </Header>
        <LegendRows />
      </Segment>
    );
  }

  // Compact: icon-button that opens a click-Popup. Uses a <span tabIndex>
  // wrapper so keyboard users can focus it. The popup also opens on hover
  // so mouse users get a preview without clicking.
  return (
    <Popup
      on={['hover', 'click', 'focus']}
      position="bottom left"
      wide
      trigger={
        <span
          tabIndex={0}
          role="button"
          aria-label={title}
          title={title}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: '#2185d0',
            ...style
          }}
        >
          <Icon name={iconName} style={{ margin: 0 }} />
        </span>
      }
    >
      <Popup.Header>{title}</Popup.Header>
      <Popup.Content>
        <LegendRows />
      </Popup.Content>
    </Popup>
  );
};

VariationStatusLegend.propTypes = {
  compact: PropTypes.bool,
  iconName: PropTypes.string,
  title: PropTypes.string,
  style: PropTypes.object
};

export default VariationStatusLegend;
